import { setVideoPlayerModal, setVideoSrc, setVideoSubtitleSrc } from '../../../../../store/workspace/slice';
import { selectVideoSrc, selectVideoSubtitleSrc } from '../../../../../store/workspace/selectors';
import { useDispatch, useSelector } from '../../../../../store/store';
import Spinner from '../../../../../components/Spinner';
import { useEffect, useRef, useState } from 'react';
import Modal from '../../../../../components/Modal';
import styles from './style.module.scss';
import axios from 'axios';

export default function VideoPlayer() {
    const dispatch = useDispatch();
    const videoSrc = useSelector(selectVideoSrc);
    const subtitleSrc = useSelector(selectVideoSubtitleSrc);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [loading, setLoading] = useState(true);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [videoObjectURL, setVideoObjectURL] = useState<string | null>(null);
    const [subtitleObjectURL, setSubtitleObjectURL] = useState<string | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (videoSrc) {
            fetchVideo();
        }
    }, [videoSrc]);

    useEffect(() => {
        if (subtitleSrc) {
            fetchSubtitle();
        }
    }, [subtitleSrc]);

    useEffect(() => {
        return () => {
            dispatch(setVideoSrc(null));
            dispatch(setVideoSubtitleSrc(null));

            if (videoObjectURL) {
                URL.revokeObjectURL(videoObjectURL);
            }

            if (subtitleObjectURL) {
                URL.revokeObjectURL(subtitleObjectURL);
            }
        }
    }, []);

    const fetchVideo = async () => {
        if (!videoSrc) return;

        try {
            setLoading(true);
            setError(false);

            console.log('Fetching video from:', videoSrc);

            const response = await axios.get(videoSrc, {
                responseType: 'blob',
                headers: {
                    "Access-Control-Allow-Origin": "*",
                }
            });

            console.log('Video downloaded, blob size:', response.data.size);
            console.log('Blob type from response:', response.data.type);

            // Use the actual content type from the response, or fallback to webm
            const blobType = response.data.type || 'video/webm';
            const correctBlob = new Blob([response.data], { type: blobType });
            const objectURL = URL.createObjectURL(correctBlob);

            console.log('Object URL created:', objectURL);
            setVideoObjectURL(objectURL);
            setLoading(false);
        } catch (e: unknown) {
            console.error('Video fetch error:', e);
            setError(true);
            setLoading(false);
        }
    };

    const convertSrtToVtt = (srtContent: string) => {
        return "WEBVTT\n\n" + srtContent
            .replace(/\r/g, '')
            .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');
    }

    const fetchSubtitle = async () => {
        if (!subtitleSrc) return;

        try {
            console.log('Fetching subtitle from:', subtitleSrc);
            const response = await fetch(subtitleSrc);
            if (!response.ok) {
                console.warn('Subtitle fetch failed:', response.status);
                return;
            }
            const text = await response.text();
            console.log('Subtitle text received, length:', text.length);
            const vttContent = convertSrtToVtt(text);
            const blob = new Blob([vttContent], { type: 'text/vtt' });
            const objectURL = URL.createObjectURL(blob);
            console.log('Subtitle Object URL created:', objectURL);
            setSubtitleObjectURL(objectURL);
        } catch (error) {
            console.error("Subtitle Fetch Error:", error);
        }
    };

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    const handleChangeSpeed = (rate: number) => {
        setPlaybackRate(rate);
    };

    return (
        <Modal
            size='xxl'
            childClassName={styles.wrapper}
            closeFunc={() => dispatch(setVideoPlayerModal(false))}
        >
            <div style={{ paddingTop: '16px' }}>
                {loading && <div className={styles.loading}><Spinner /></div>}

                {error && (
                    <div className={styles.error}>
                        Failed to load video. Please try again.
                    </div>
                )}

                {!loading && !error && videoObjectURL && (
                    <div className={styles.player}>
                        <video
                            ref={videoRef}
                            src={videoObjectURL}
                            controls
                            controlsList="nodownload"
                            crossOrigin="anonymous"
                            style={{ width: '100%', height: '100%' }}
                        >
                            {subtitleObjectURL && (
                                <track
                                    kind="subtitles"
                                    src={subtitleObjectURL}
                                    srcLang="en"
                                    label="English"
                                    default
                                />
                            )}
                        </video>
                        <div className={styles.player_speed}>
                            <button
                                className={styles.player_speed_btn}
                                data-is-selected={playbackRate === 1}
                                onClick={() => handleChangeSpeed(1)}
                            >
                                1x
                            </button>
                            <button
                                className={styles.player_speed_btn}
                                data-is-selected={playbackRate === 1.5}
                                onClick={() => handleChangeSpeed(1.5)}
                            >
                                1.5x
                            </button>
                            <button
                                className={styles.player_speed_btn}
                                data-is-selected={playbackRate === 2}
                                onClick={() => handleChangeSpeed(2)}
                            >
                                2x
                            </button>
                            <button
                                className={styles.player_speed_btn}
                                data-is-selected={playbackRate === 3}
                                onClick={() => handleChangeSpeed(3)}
                            >
                                3x
                            </button>
                            <button
                                className={styles.player_speed_btn}
                                data-is-selected={playbackRate === 4}
                                onClick={() => handleChangeSpeed(4)}
                            >
                                4x
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    )
}
